import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Mail, User, LogIn, UserPlus, AlertTriangle, Clock } from 'lucide-react';
import { API_URL } from '../utils/apiConfig';

const SignatureAuthHandler = ({ children, documentId }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [authState, setAuthState] = useState('loading');
  const [signatureToken, setSignatureToken] = useState(null);
  const [signerEmail, setSignerEmail] = useState('');
  const [tokenError, setTokenError] = useState(null);
  const [showAuthOptions, setShowAuthOptions] = useState(false);

  // Check for signature token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    
    if (token) {
      validateSignatureToken(token);
    } else if (user) {
      // Regular authenticated user
      setAuthState('authenticated');
    } else {
      // No token and no user - redirect to login
      setAuthState('unauthorized');
    }
  }, [searchParams, user]);

  const validateSignatureToken = async (token) => {
    try {
      setAuthState('validating');
      
      // Make a test request to validate the token
      const response = await fetch(`${API_URL}/documents/${documentId}?token=${token}`, {
        headers: user ? {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        } : {}
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.signatureAuth) {
          setSignatureToken(token);
          setSignerEmail(data.signatureAuth.signerEmail);
          
          // Check authentication state
          if (data.signatureAuth.hasLoggedInUser && data.signatureAuth.userEmailMatches) {
            setAuthState('token_user_match');
          } else if (data.signatureAuth.hasLoggedInUser && !data.signatureAuth.userEmailMatches) {
            setAuthState('token_user_mismatch');
            setShowAuthOptions(true);
          } else {
            setAuthState('token_only');
          }
        } else {
          setAuthState('authenticated');
        }
      } else {
        const errorData = await response.json();
        setTokenError(errorData.message || 'Invalid signature link');
        
        if (errorData.tokenExpired) {
          setAuthState('token_expired');
        } else if (errorData.tokenInvalid) {
          setAuthState('token_invalid');
        } else {
          setAuthState('token_error');
        }
      }
    } catch (error) {
      console.error('Error validating signature token:', error);
      setTokenError('Failed to validate signature link');
      setAuthState('token_error');
    }
  };

  const handleContinueAsCurrentUser = () => {
    setAuthState('authenticated');
    setShowAuthOptions(false);
  };

  const handleSwitchToSignerAccount = () => {
    // Log out current user and continue with signature token
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuthState('token_only');
    setShowAuthOptions(false);
    window.location.reload(); // Refresh to clear auth context
  };

  const handleCreateAccount = () => {
    // Signup is disabled - show message instead
    alert('New user registrations are currently disabled. Please contact support for access.');
  };

  const handleLogin = () => {
    // Store the signature token for after login
    localStorage.setItem('pendingSignatureToken', signatureToken);
    localStorage.setItem('pendingDocumentId', documentId);
    navigate('/?login=true');
  };

  // Render different states
  if (authState === 'loading' || authState === 'validating') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {authState === 'loading' ? 'Loading...' : 'Validating signature link...'}
          </h2>
          <p className="text-gray-600">Please wait while we verify your access.</p>
        </div>
      </div>
    );
  }

  if (authState === 'token_expired') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <Clock className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Signature Link Expired</h2>
          <p className="text-gray-600 mb-6">
            This signature link has expired. Please contact the document sender to request a new signature link.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (authState === 'token_invalid' || authState === 'token_error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Signature Link</h2>
          <p className="text-gray-600 mb-6">
            {tokenError || 'This signature link is not valid. Please check the link or contact the document sender.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (authState === 'token_user_mismatch' && showAuthOptions) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-lg w-full">
          <div className="text-center mb-6">
            <User className="h-16 w-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Choose How to Continue</h2>
            <p className="text-gray-600">
              You're currently signed in as <strong>{user?.email}</strong>, but this signature request 
              is for <strong>{signerEmail}</strong>.
            </p>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleContinueAsCurrentUser}
              className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <User className="w-5 h-5 mr-2" />
              Continue as {user?.name || user?.email}
            </button>
            
            <button
              onClick={handleSwitchToSignerAccount}
              className="w-full flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              <Mail className="w-5 h-5 mr-2" />
              Switch to sign as {signerEmail}
            </button>
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              Your choice will determine which account is associated with the signature.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (authState === 'token_only') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-lg w-full">
          <div className="text-center mb-6">
            <Mail className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Ready to Sign</h2>
            <p className="text-gray-600">
              You can review and sign this document as <strong>{signerEmail}</strong>.
            </p>
            <p className="text-xs text-gray-500 mt-2">This link remains active for 48 hours.</p>
          </div>

          <div className="space-y-4 mb-6">
            <button
              onClick={() => setAuthState('authenticated')}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Continue to Document
            </button>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <p className="text-sm text-gray-600 text-center mb-4">
              Want to save this signature to your account?
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleLogin}
                className="flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </button>
              <button
                onClick={handleCreateAccount}
                className="flex items-center justify-center px-4 py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (authState === 'unauthorized') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600 mb-6">
            Please sign in to access this document.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <LogIn className="w-4 h-4 mr-2" />
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // If authenticated (either through regular auth or valid signature token), render children
  return children;
};

export default SignatureAuthHandler; 